/**
 * F18 — NIPE tool: kickAdminConsent
 *
 * CRITICAL: per Round 1 hard limit, NIPE MUST NEVER programmatically
 * redirect the user to a Microsoft URL via `window.location.href = ...`.
 * Instead, this tool surfaces an `<a href={...}>` for the user to click.
 *
 * The handler returns the consent URL + stashes wizard intent in
 * sessionStorage (via the context callback) so the landing surface can
 * resume the wizard. The actual click happens via UI in the wizard
 * route; NIPE proposes the link but the user navigates.
 *
 * `mutatesRemote: true` because the click eventually changes app-reg
 * consent state in the target tenant — a real Microsoft side effect.
 * `requiresApproval: true` so the runner can surface the proposed
 * action in the F3 pending-writes tray for explicit approval if the
 * SPA wires it that way.
 */

import type { NipeTool } from './types';

export interface KickAdminConsentContext {
  /** Optional. Only present when the SPA wires the F18 wizard. */
  kickAdminConsent?: (args: {
    tenantId: string;
    returnPath: string;
  }) => Promise<{
    consentUrl: string;
    intentStashed: boolean;
  }>;
}

export const kickAdminConsentTool: NipeTool<KickAdminConsentContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'kickAdminConsent',
      description:
        'Prepare the admin-consent link for a Microsoft tenant. Stashes the wizard return-state in sessionStorage; returns a URL the USER will click. NIPE must surface this as a clickable link, NEVER as a programmatic redirect.',
      parameters: {
        type: 'object',
        properties: {
          tenantId: {
            type: 'string',
            description: 'Target tenant GUID (from a prior inspectTenant kind=domain probe).',
          },
          returnPath: {
            type: 'string',
            description: 'Path within Repancy to land after the Microsoft redirect (e.g., "/onboard/new").',
          },
        },
        required: ['tenantId', 'returnPath'],
      },
    },
  },
  mutatesRemote: true,
  requiresApproval: true,
  handler: async (args, ctx) => {
    if (!ctx.kickAdminConsent) {
      return { error: 'Admin-consent kick-off is not available in this session.' };
    }
    const tenantId = typeof args.tenantId === 'string' ? args.tenantId : '';
    const returnPath = typeof args.returnPath === 'string' ? args.returnPath : '';
    if (!tenantId || !returnPath) {
      return { error: 'tenantId and returnPath required' };
    }
    return await ctx.kickAdminConsent({ tenantId, returnPath });
  },
};
